import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ClipboardList, Users, Package, BarChart3, Wrench, ShieldCheck,
  CheckCircle, ArrowRight, FileText, AlertTriangle, ChevronDown,
  MessageSquare, QrCode, RefreshCcw, Clock, Eye, Smartphone,
  TrendingUp, Star, Mail, ArrowDown
} from 'lucide-react';

/* ─── Scroll Reveal Hook ─── */
const useReveal = (opts = {}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.unobserve(el); } },
      { threshold: 0.15, ...opts }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, visible];
};

const Reveal = ({ children, className = '', delay = 0, direction = 'up' }) => {
  const [ref, visible] = useReveal();
  const dirs = { up: 'translate-y-8', down: '-translate-y-8', left: 'translate-x-8', right: '-translate-x-8' };
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-x-0 translate-y-0' : `opacity-0 ${dirs[direction]}`} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const IMGS = {
  logo: 'https://customer-assets.emergentagent.com/job_verso-autoreports/artifacts/b11ds7pz_3.png',
  dashboardDark: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/l0kt3gz5_orden_dashboard-dark.webp',
  dashboardDesktop: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/sdetad95_dashboard_desktop.webp',
  dashboardMobile: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/rr1wkuuy_darhboard_celular_dark.webp',
  ordenDesktop: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/h82u6g3b_orden_desktop.webp',
  ordenDesktop2: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/5khljads_orden_desktop2.webp',
  ordenesKanban: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/vux8ck76_ordenes_desktop.webp',
  tableroDark: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/7mas2j24_tablero_vehiculos_en_el_taller-dark.webp',
  mejorPrecio: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/4m5777o0_mejor_precio_proveedor_desktop.webp',
  crmDark: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/acpz9sas_crm_dark.webp',
  crmLight: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/cn4ocz8v_CRM_DESKTOP.webp',
  cotizador: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/89j6rhxh_cotizador_de_repuestos-dark.webp',
  clienteNoche: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/avbev6dn_cliente_link_cotizacion_noche.webp',
  clienteDia: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/ylo1nmv9_cliente_link_cotizacion_dia.webp',
  qrDia: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/1lpijdz7_qr_cliente_plandemantenimiento_dia.webp',
  qrNoche: 'https://customer-assets-7cd3h4nn.emergentagent.net/job_be8972e1-9b2a-40a0-99d4-1b2efed42d9e/artifacts/7owskxz8_qr_cliente_plandemantenimiento_noche.webp',
};

const DEMO_URL = 'https://app.versosoftech.com';
const WHATSAPP_URL = 'https://wa.me/573234041480';

/* ═══════════════════════════════════════
   1. HERO
   ═══════════════════════════════════════ */
const HeroSection = () => {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20" data-testid="hero-section">
      {/* Mesh gradient background */}
      <div className="absolute inset-0 bg-[#0A0817]" />
      <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#4F46FF]/8 rounded-full blur-[160px]" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#747CF5]/6 rounded-full blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 w-full">
        <div className="text-center max-w-4xl mx-auto">
          <Reveal>
            <h1 className="font-headline text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-white mb-6">
              Digitaliza tu taller.
              <br />
              <span className="text-gradient-forge">Tu cliente aprueba desde el celular.</span>
            </h1>
          </Reveal>

          <Reveal delay={150}>
            <p className="font-body text-base sm:text-lg text-[#A8A3B8] max-w-2xl mx-auto mb-10 leading-relaxed">
              Software de gestión para talleres mecánicos y servitecas en Colombia. Órdenes de servicio, 
              planes de mantenimiento, control de calidad y seguimiento en tiempo real para tu cliente. 
              Todo en una sola plataforma.
            </p>
          </Reveal>

          <Reveal delay={300}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-5">
            <Button
              onClick={() => window.open(DEMO_URL, '_blank')}
              className="gradient-forge text-white font-semibold rounded-lg px-8 py-4 text-base hover:opacity-90 transition-opacity shadow-xl shadow-[#4F46FF]/25 w-full sm:w-auto"
              data-testid="hero-cta-demo"
            >
              Pide tu DEMO gratis
            </Button>
            <Button
              onClick={() => scrollTo('#producto')}
              variant="outline"
              className="border-[#4F46FF]/50 text-white hover:bg-[#4F46FF]/10 rounded-lg px-8 py-4 text-base w-full sm:w-auto bg-transparent"
              data-testid="hero-cta-how"
            >
              Ver cómo funciona
              <ArrowDown className="ml-2" size={16} />
            </Button>
          </div>

          <p className="font-caption text-[10px] sm:text-xs uppercase tracking-[0.12em] text-[#A8A3B8]/60">
            Sin tarjeta de crédito · Prueba gratuita de 30 días
          </p>
          </Reveal>
        </div>

        {/* Dashboard mockup */}
        <Reveal delay={450}>
          <div className="mt-16 max-w-5xl mx-auto relative group">
          <div className="absolute -inset-4 bg-gradient-to-r from-[#4F46FF]/10 to-[#747CF5]/10 rounded-3xl blur-2xl opacity-60 group-hover:opacity-80 transition-opacity" />
          <div className="relative bg-[#1B1530] border border-white/10 rounded-2xl overflow-hidden p-1">
            <img src={IMGS.dashboardDark} alt="Dashboard VERSO Talleres" className="w-full rounded-xl" />
          </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════
   2. SOCIAL PROOF BAR
   ═══════════════════════════════════════ */
const SocialProof = () => (
  <section className="relative py-8 bg-[#1B1530] border-y border-white/5" data-testid="social-proof-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
      <span className="font-caption text-xs uppercase tracking-[0.12em] text-[#A8A3B8]/60">Usado en talleres reales en Colombia</span>
      <span className="hidden sm:inline text-white/20">·</span>
      <span className="text-white text-sm font-medium">Validado en Servinyección, Tuluá — Valle del Cauca</span>
    </div>
  </section>
);

/* ═══════════════════════════════════════
   3. PROBLEMA
   ═══════════════════════════════════════ */
const ProblemSection = () => {
  const problems = [
    { icon: ClipboardList, title: 'Órdenes perdidas', desc: 'Papel, Excel, WhatsApp. Nada se conecta. Las órdenes se pierden y los repuestos se olvidan.', color: '#F26060' },
    { icon: Eye, title: 'Sin control del proceso', desc: 'No sabes en qué estado está cada carro. Tu cliente te llama 5 veces para preguntar "¿cómo va?"', color: '#F5B300' },
    { icon: BarChart3, title: 'Sin datos reales', desc: 'No sabes cuánto ganaste este mes, qué servicio es el más rentable, ni qué mecánico rinde más.', color: '#7C61BD' },
    { icon: RefreshCcw, title: 'Sin plan de mantenimiento', desc: 'El cliente se va y no vuelve. No tienes manera de avisarle cuándo le toca el próximo servicio.', color: '#3FBFA8' },
  ];
  return (
    <section className="relative py-24 sm:py-32 bg-[#0A0817]" data-testid="problem-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-16">
            <p className="font-caption text-xs uppercase tracking-[0.12em] text-[#4F46FF] mb-4">El problema</p>
            <h2 className="font-headline text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
              El 95% de los talleres en Colombia trabajan en papel o Excel
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {problems.map((p, i) => (
            <Reveal key={i} delay={i * 100}>
              <Card className="bg-[#1B1530] border border-white/5 rounded-2xl p-7 hover:border-white/10 transition-all duration-300" data-testid={`problem-card-${i}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${p.color}15` }}>
                <p.icon style={{ color: p.color }} size={20} />
              </div>
              <h3 className="font-headline text-base font-semibold text-white mb-2">{p.title}</h3>
              <p className="font-body text-[#A8A3B8] text-sm leading-relaxed">{p.desc}</p>
            </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════
   4. EL PRODUCTO — VERSO TALLERES
   ═══════════════════════════════════════ */
const ProductFlow = () => {
  const steps = [
    { num: '01', title: 'Ingreso', desc: 'Se registra el vehículo, cliente, km y fotos del estado inicial.', color: '#F26060' },
    { num: '02', title: 'Diagnóstico', desc: 'El mecánico analiza y define servicios, repuestos y evidencias.', color: '#F5B300' },
    { num: '03', title: 'Cotización', desc: 'Se consolida todo y se cotiza con proveedores.', color: '#7C61BD' },
    { num: '04', title: 'Aprobación', desc: 'El cliente aprueba o rechaza desde su celular.', color: '#4F46FF' },
    { num: '05', title: 'Reparación', desc: 'Los servicios avanzan cuando los repuestos llegan.', color: '#3FBFA8' },
    { num: '06', title: 'Control de calidad', desc: 'Checklists obligatorios antes de entregar.', color: '#3DCB7A' },
    { num: '07', title: 'Entrega', desc: 'Se cierra la orden y el historial queda guardado.', color: '#00E5D0' },
  ];
  return (
    <div className="mb-24">
      <h3 className="font-headline text-xl sm:text-2xl font-semibold text-white text-center mb-12">
        Flujo de una orden de servicio
      </h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
        {steps.map((s, i) => (
          <Reveal key={i} delay={i * 80}>
            <div className="text-center group" data-testid={`flow-step-${i}`}>
            <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-3 border transition-all duration-300 group-hover:scale-110" style={{ borderColor: `${s.color}40`, backgroundColor: `${s.color}10` }}>
              <span className="font-caption text-xs font-bold" style={{ color: s.color }}>{s.num}</span>
            </div>
            <h4 className="font-headline text-sm font-semibold text-white mb-1">{s.title}</h4>
            <p className="text-[#A8A3B8] text-xs leading-relaxed">{s.desc}</p>
          </div>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

const ProductModules = () => {
  const modules = [
    { icon: ClipboardList, title: 'Órdenes de servicio', desc: 'Flujo completo: diagnóstico → cotización → aprobación → reparación → entrega. Dos tipos: Normal (completa) y Básica (rápida).', img: IMGS.ordenesKanban },
    { icon: MessageSquare, title: 'CRM de alertas', desc: 'Detecta automáticamente qué vehículos necesitan mantenimiento y te permite notificar al cliente por WhatsApp.', img: IMGS.crmDark },
    { icon: Package, title: 'Cotizador de proveedores', desc: 'Compara precios de diferentes proveedores para el mismo repuesto. Elige el mejor precio con un clic.', img: IMGS.mejorPrecio },
    { icon: BarChart3, title: 'Dashboard', desc: 'Ingresos vs egresos, estado del taller en porcentajes, órdenes activas por estado. Todo en tiempo real.', img: IMGS.dashboardDesktop },
    { icon: FileText, title: 'Historial vehicular', desc: 'Todos los servicios, repuestos e insumos de todas las órdenes. Filtrable por fecha, técnico, cliente o vehículo.', img: IMGS.tableroDark },
    { icon: Wrench, title: 'Inventario', desc: 'Control de repuestos e insumos del taller. Stock en tiempo real con alertas de mínimos.', img: IMGS.ordenDesktop2 },
    { icon: TrendingUp, title: 'Integración contable', desc: 'Conecta con SIIGO o Alegra para facturación automática y sincronización de inventario.', img: null },
  ];
  return (
    <div className="mb-24">
      <h3 className="font-headline text-xl sm:text-2xl font-semibold text-white text-center mb-4">
        Módulos del sistema
      </h3>
      <p className="text-[#A8A3B8] text-center mb-12 max-w-xl mx-auto text-sm">
        Cada módulo diseñado para resolver un problema real de tu taller.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {modules.map((m, i) => (
          <Reveal key={i} delay={i * 80}>
            <Card className="bg-[#1B1530] border border-white/5 rounded-2xl overflow-hidden hover:border-[#4F46FF]/30 transition-all duration-300 group h-full" data-testid={`module-card-${i}`}>
            {m.img && (
              <div className="h-44 overflow-hidden border-b border-white/5">
                <img src={m.img} alt={m.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
              </div>
            )}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-[#4F46FF]/10 flex items-center justify-center">
                  <m.icon className="text-[#4F46FF]" size={18} />
                </div>
                <h4 className="font-headline text-sm font-semibold text-white">{m.title}</h4>
              </div>
              <p className="text-[#A8A3B8] text-sm leading-relaxed">{m.desc}</p>
            </div>
          </Card>
          </Reveal>
        ))}
      </div>
    </div>
  );
};

const PhoneMockup = ({ src, alt, className = '' }) => (
  <div className={`relative ${className}`}>
    <div className="relative bg-[#0A0817] rounded-[2rem] border-2 border-white/10 p-2 shadow-2xl shadow-[#4F46FF]/10">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-[#0A0817] rounded-b-xl border-x-2 border-b-2 border-white/10 z-10" />
      <div className="rounded-[1.5rem] overflow-hidden bg-[#1B1530]">
        <img src={src} alt={alt} className="w-full" />
      </div>
    </div>
  </div>
);

const ClientExperience = () => (
  <div className="space-y-16 mb-8">
    {/* Experiencia del cliente */}
    <Reveal>
      <div className="bg-[#1B1530] border border-white/5 rounded-3xl p-8 sm:p-12" data-testid="client-experience-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="font-caption text-xs uppercase tracking-[0.12em] text-[#00E5D0] mb-4">Experiencia del cliente</p>
          <h3 className="font-headline text-2xl sm:text-3xl font-bold text-white mb-4">
            Tu cliente no está a ciegas
          </h3>
          <p className="text-[#A8A3B8] mb-6 leading-relaxed">
            Desde su celular puede ver el diagnóstico con fotos, aprobar la cotización 
            y seguir el avance en tiempo real. Sin llamadas, sin confusión.
          </p>
          <div className="space-y-3">
            {['Ve el diagnóstico con fotos reales', 'Aprueba o rechaza la cotización', 'Sigue el avance en tiempo real'].map((item) => (
              <div key={item} className="flex items-center gap-3 text-sm text-white/80">
                <CheckCircle className="text-[#3DCB7A] flex-shrink-0" size={16} />
                {item}
              </div>
            ))}
          </div>
          <div className="mt-6 bg-[#4F46FF]/10 border border-[#4F46FF]/20 rounded-xl p-4">
            <p className="text-white text-sm font-medium italic">
              "Esto genera confianza y transparencia total. Y hace que el cliente vuelva solo."
            </p>
          </div>
        </div>
        <div className="flex justify-center gap-5 sm:gap-8">
          <PhoneMockup src={IMGS.clienteDia} alt="Cotización del cliente - día" className="w-[140px] sm:w-[170px]" />
          <PhoneMockup src={IMGS.clienteNoche} alt="Cotización del cliente - noche" className="w-[140px] sm:w-[170px] mt-10" />
        </div>
      </div>
      </div>
    </Reveal>

    {/* Planes de mantenimiento */}
    <Reveal>
      <div className="bg-[#1B1530] border border-white/5 rounded-3xl p-8 sm:p-12" data-testid="maintenance-plans-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="flex justify-center gap-5 sm:gap-8 order-2 lg:order-1">
          <PhoneMockup src={IMGS.qrDia} alt="Plan de mantenimiento QR - día" className="w-[140px] sm:w-[170px]" />
          <PhoneMockup src={IMGS.qrNoche} alt="Plan de mantenimiento QR - noche" className="w-[140px] sm:w-[170px] mt-10" />
        </div>
        <div className="order-1 lg:order-2">
          <p className="font-caption text-xs uppercase tracking-[0.12em] text-[#F5B300] mb-4">Planes de mantenimiento</p>
          <h3 className="font-headline text-2xl sm:text-3xl font-bold text-white mb-4">
            Que el cliente vuelva sin que lo busques
          </h3>
          <p className="text-[#A8A3B8] mb-6 leading-relaxed">
            Cada vehículo tiene su plan de mantenimiento con QR compartible. Tu cliente escanea, 
            ve cuándo le toca el próximo servicio y agenda directamente.
          </p>
          <div className="space-y-3">
            {['QR único por vehículo', 'Recordatorios automáticos al cliente', 'Fidelización sin esfuerzo'].map((item) => (
              <div key={item} className="flex items-center gap-3 text-sm text-white/80">
                <CheckCircle className="text-[#3DCB7A] flex-shrink-0" size={16} />
                {item}
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>
    </Reveal>
  </div>
);

const ProductSection = () => (
  <section id="producto" className="relative py-24 sm:py-32 bg-[#0A0817]" data-testid="product-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <Reveal>
          <p className="font-caption text-xs uppercase tracking-[0.12em] text-[#4F46FF] mb-4">El producto</p>
          <h2 className="font-headline text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">
            Todo lo que tu taller necesita. En una sola plataforma.
          </h2>
          <p className="text-[#A8A3B8] max-w-2xl mx-auto">
            Desde el diagnóstico hasta la entrega del vehículo. Sin papel, sin desorden, sin excusas.
          </p>
        </Reveal>
      </div>
      <ProductFlow />
      <ProductModules />
      <ClientExperience />
    </div>
  </section>
);

/* ═══════════════════════════════════════
   5. PLANES Y PRECIOS
   ═══════════════════════════════════════ */
const PlansSection = () => {
  const plans = [
    {
      name: 'Plan Serviteca', price: '$49.000', period: 'COP/mes',
      target: 'Servitecas y centros de cambio rápido',
      features: ['Órdenes básicas', 'Hasta 2 usuarios', 'Historial vehicular', 'Soporte WhatsApp'],
      popular: false,
    },
    {
      name: 'Plan Básico', price: '$89.000', period: 'COP/mes',
      target: 'Talleres pequeños que están empezando',
      features: ['Órdenes completas', 'Hasta 3 usuarios', 'CRM de alertas', 'Dashboard básico', 'Soporte WhatsApp'],
      popular: false,
    },
    {
      name: 'Taller Pro', price: '$159.000', period: 'COP/mes',
      target: 'Talleres medianos (2-8 técnicos)',
      features: ['Todo en Básico', 'Usuarios ilimitados', 'Cotizador proveedores', 'Control de calidad', 'Planes mantenimiento', 'Dashboard avanzado', 'Integración contable'],
      popular: true,
    },
    {
      name: 'Taller Premium', price: '$329.000', period: 'COP/mes por sede',
      target: 'Talleres grandes y multisede',
      features: ['Todo en Pro', 'Multi-sede', 'API personalizada', 'Onboarding asistido', 'Soporte prioritario', 'Reportes personalizados'],
      popular: false,
    },
  ];

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="planes" className="relative py-24 sm:py-32 bg-[#0A0817]" data-testid="plans-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-16">
            <p className="font-caption text-xs uppercase tracking-[0.12em] text-[#4F46FF] mb-4">Planes y precios</p>
            <h2 className="font-headline text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">
              Un plan para cada tipo de taller
            </h2>
            <p className="text-[#A8A3B8] max-w-xl mx-auto">
              Precios en pesos colombianos. Sin sorpresas. Sin contratos de permanencia.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {plans.map((plan, i) => (
            <Reveal key={i} delay={i * 100}>
              <Card
              key={i}
              className={`rounded-2xl p-7 transition-all duration-300 relative ${
                plan.popular
                  ? 'bg-[#1B1530] border-2 border-[#4F46FF] hover:-translate-y-1 shadow-xl shadow-[#4F46FF]/10'
                  : 'bg-[#1B1530] border border-white/5 hover:border-white/10 hover:-translate-y-1'
              }`}
              data-testid={`plan-card-${i}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="gradient-forge text-white text-[10px] font-caption uppercase tracking-[0.12em] font-bold px-4 py-1.5 rounded-full">
                    Más popular
                  </span>
                </div>
              )}
              <h3 className="font-headline text-base font-semibold text-white mb-1">{plan.name}</h3>
              <p className="text-[#A8A3B8] text-xs mb-4">{plan.target}</p>
              <div className="mb-5">
                <span className="font-headline text-3xl font-bold text-white">{plan.price}</span>
                <span className="text-[#A8A3B8] text-xs ml-1.5">{plan.period}</span>
              </div>
              <ul className="space-y-2.5 mb-7">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-[#A8A3B8]">
                    <CheckCircle className="text-[#3DCB7A] flex-shrink-0 mt-0.5" size={14} />
                    {f}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => window.open(DEMO_URL, '_blank')}
                className={`w-full rounded-lg py-3 font-medium text-sm ${
                  plan.popular
                    ? 'gradient-forge text-white hover:opacity-90 shadow-lg shadow-[#4F46FF]/20'
                    : 'border border-white/10 text-white hover:bg-white/5 bg-transparent'
                }`}
              >
                Empezar prueba gratis
              </Button>
            </Card>
            </Reveal>
          ))}
        </div>

        <p className="text-center text-[#A8A3B8]/60 text-xs mt-8">
          Pago anual disponible con 2 meses gratis. Todos los precios incluyen soporte técnico por WhatsApp.
        </p>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════
   6. VERSO REPORTES — PRÓXIMAMENTE
   ═══════════════════════════════════════ */
const ReportesSection = () => {
  const [email, setEmail] = useState('');
  return (
    <section id="reportes" className="relative py-24 sm:py-32 overflow-hidden" data-testid="reportes-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#FF2D7A]/8 via-[#0A0817] to-[#7B61FF]/8" />
      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <Reveal>
          <p className="font-caption text-xs uppercase tracking-[0.12em] text-[#FF2D7A] mb-4">Nuevo producto</p>
          <h2 className="font-headline text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-3">
            VERSO REPORTES
          </h2>
          <p className="font-headline text-xl sm:text-2xl font-light text-[#A8A3B8] mb-6">
            Próximamente
          </p>
          <p className="text-[#A8A3B8] mb-10 max-w-lg mx-auto leading-relaxed">
            Reportes vehiculares para Colombia. Pronto podrás consultar el historial de mantenimiento 
            completo de cualquier vehículo.
          </p>
        </Reveal>
        <Reveal delay={200}>
        <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
          <Input
            type="email"
            placeholder="Tu correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="bg-[#1B1530] border-white/10 text-white placeholder:text-[#A8A3B8]/50 rounded-lg h-12 focus:border-[#FF2D7A] focus:ring-[#FF2D7A]"
            data-testid="reportes-email-input"
          />
          <Button
            className="bg-gradient-to-r from-[#FF2D7A] to-[#7B61FF] text-white font-medium rounded-lg px-6 h-12 hover:opacity-90 whitespace-nowrap"
            data-testid="reportes-notify-btn"
          >
            <Mail className="mr-2" size={16} />
            Avísame
          </Button>
        </div>
        </Reveal>
      </div>
    </section>
  );
};

/* ═══════════════════════════════════════
   7. TESTIMONIOS
   ═══════════════════════════════════════ */
const TestimonialsSection = () => (
  <section className="relative py-24 sm:py-32 bg-[#0A0817]" data-testid="testimonials-section">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <Reveal>
          <p className="font-caption text-xs uppercase tracking-[0.12em] text-[#4F46FF] mb-4">Prueba social</p>
          <h2 className="font-headline text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">
            Lo que dicen los talleres
          </h2>
        </Reveal>
      </div>

      <Reveal delay={150}>
        <Card className="bg-[#1B1530] border border-white/5 rounded-3xl p-8 sm:p-12 text-center" data-testid="testimonial-card">
        <div className="flex justify-center gap-1 mb-6">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="text-[#F5B300] fill-[#F5B300]" size={20} />
          ))}
        </div>
        <blockquote className="font-body text-lg sm:text-xl text-white leading-relaxed mb-8 max-w-2xl mx-auto">
          "Antes perdíamos órdenes en papel todos los días. Con VERSO todo queda registrado 
          y mis clientes pueden aprobar desde el celular. Es otro nivel."
        </blockquote>
        <div>
          <p className="text-white font-medium">Servinyección</p>
          <p className="text-[#A8A3B8] text-sm">Tuluá, Valle del Cauca</p>
        </div>
        </Card>
      </Reveal>

      <Reveal delay={300}>
        <div className="grid grid-cols-3 gap-6 mt-10 max-w-lg mx-auto">
        {[
          { value: '+200', label: 'órdenes gestionadas' },
          { value: '0', label: 'órdenes perdidas' },
          { value: '100%', label: 'digital' },
        ].map((stat, i) => (
          <div key={i} className="text-center" data-testid={`testimonial-stat-${i}`}>
            <div className="font-headline text-2xl font-bold text-gradient-forge">{stat.value}</div>
            <div className="text-[#A8A3B8] text-xs mt-1">{stat.label}</div>
          </div>
        ))}
        </div>
      </Reveal>
    </div>
  </section>
);

/* ═══════════════════════════════════════
   8. CTA FINAL
   ═══════════════════════════════════════ */
const FinalCTA = () => (
  <section id="contacto" className="relative py-24 sm:py-32 overflow-hidden" data-testid="final-cta-section">
    <div className="absolute inset-0 gradient-forge opacity-90" />
    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] opacity-40" />
    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <Reveal>
        <h2 className="font-headline text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white mb-4">
          Deja de perder plata en papel
        </h2>
        <p className="text-white/80 text-base sm:text-lg mb-10 max-w-xl mx-auto leading-relaxed">
          Digitaliza tu taller en 15 minutos. Prueba gratis por 30 días. Sin tarjeta de crédito.
        </p>
      </Reveal>
      <Reveal delay={200}>
        <Button
        onClick={() => window.open(DEMO_URL, '_blank')}
        className="bg-white text-[#4F46FF] font-semibold rounded-lg px-10 py-4 text-base hover:bg-white/90 transition-opacity shadow-xl"
        data-testid="final-cta-demo"
      >
        Pide tu DEMO gratis
      </Button>
      <div className="mt-6">
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noreferrer"
          className="text-white/70 hover:text-white text-sm underline underline-offset-4 transition-colors"
        >
          O escríbenos por WhatsApp
        </a>
      </div>
      </Reveal>
    </div>
  </section>
);

/* ═══════════════════════════════════════
   HOME PAGE
   ═══════════════════════════════════════ */
export const Home = () => (
  <div className="bg-[#0A0817]">
    <HeroSection />
    <SocialProof />
    <ProblemSection />
    <ProductSection />
    <PlansSection />
    <ReportesSection />
    <TestimonialsSection />
    <FinalCTA />
  </div>
);
